/*Page_Registration_Login.tsx*/
import { useState, FormEvent, useEffect } from 'react';
import './Page_Registration_Login.css';
import { AlertCircle, User, Lock, Eye, EyeOff, Loader2, Mail, Phone, CheckCircle } from 'lucide-react';
import { queryDomain } from '../Services/QueryDomain';
import { selfRegistration, NonFederatedRegistrationData } from '../Services/SelfRegistration';
import { checkRegistration } from '../Services/CheckRegistration';
import { resendInvite } from '../Services/ResendInvite';
interface RegistrationLoginProps {
  onLoginRedirect: (typedEmail: string) => void;

}

function Page_Registration_Login({ onLoginRedirect }: RegistrationLoginProps) {
  const [view, setView] = useState<'initial' | 'register-form' | 'success-federated' | 'success-nonfederated' | 'already-registered'>('initial');
  const [email, setEmail] = useState<string>('');
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [isFederated, setIsFederated] = useState<boolean>(false);

  const [isResending, setIsResending] = useState<boolean>(false);
  const [resendMessage, setResendMessage] = useState<string>('');

  // Non-federated form fields
  const [formData, setFormData] = useState<NonFederatedRegistrationData>({
    userEmail: '',
    firstName: '',
    lastName: '',
    phoneNumber: '',
    password: ''
  });

  const [passwordErrors, setPasswordErrors] = useState<string[]>([]);

  //terms and conditions
  const [agreedToTerms, setAgreedToTerms] = useState<boolean>(false);

  const validatePassword = (password: string): string[] => {
    const errors: string[] = [];

    if (password.length < 8 || password.length > 64) {
      errors.push('Password must be between 8 and 64 characters');
    }

    if (password.startsWith(' ') || password.endsWith(' ')) {
      errors.push('Password cannot start or end with a space');
    }

    // Check complexity (at least 3 out of 4 types)
    let complexityCount = 0;
    if (/[a-z]/.test(password)) complexityCount++;
    if (/[A-Z]/.test(password)) complexityCount++;
    if (/[0-9]/.test(password)) complexityCount++;
    if (/[@#$%^&*\-_!+=\[\]{}|\\:',.\?/`~"();<>]/.test(password)) complexityCount++;

    if (complexityCount < 3) {
      errors.push('Password must contain at least 3 of: lowercase, uppercase, number, special character');
    }

    // Check for disallowed characters
    const allowedPattern = /^[A-Za-z0-9@#$%^&*\-_!+=\[\]{}|\\:',.\?/`~"();<> ]+$/;
    if (!allowedPattern.test(password)) {
      errors.push('Password contains invalid characters');
    }

    return errors;
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newPassword = e.target.value;
    setFormData(prev => ({ ...prev, password: newPassword }));
    setPasswordErrors(validatePassword(newPassword));
    if (error) setError('');
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (error) setError('');
  };

  const extractDomain = (email: string): string => {
    const parts = email.split('@');
    return parts.length === 2 ? parts[1] : '';
  };



  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      // Step 1: Check if user is already registered
      const registrationCheck = await checkRegistration(email);

      if (registrationCheck.isRegistered) {
        // User already registered - redirect directly to Microsoft login
        setIsLoading(false);
        onLoginRedirect(email);
        return;
      }

      // Step 2: Query domain if not registered
      const response = await queryDomain(email);

      if (!response.success) {
        if (response.description?.includes('not present')) {
          setError('This domain is not yet onboarded. Please contact the integration team.');
          setIsLoading(false);
          return;
        }
        setError(response.message || 'Failed to verify domain');
        setIsLoading(false);
        return;
      }

      const authPattern = response.authPattern;

      if (authPattern?.toLowerCase() === 'azure/entra id' ||
        authPattern?.toLowerCase() === 'saml 3rd-party idp' ||
        authPattern?.toLowerCase() === 'email - one time password' ||
        authPattern?.toLowerCase() === 'social-idp'
      ) {
        setIsFederated(true);
        await handleFederatedRegistration(email);
      } else if (authPattern?.toLowerCase() === 'email and password') {

        setIsFederated(false);
        setFormData(prev => ({ ...prev, userEmail: email }));
        setView('register-form');
      } else {
        setError('Unsupported authentication pattern. Please contact support.');
      }
    } catch (err: any) {
      setError(err.message || 'An error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendInvite = async () => {
    if (!email.trim()) {
      setError('Please enter your email address first');
      return;
    }

    setIsResending(true);
    setError('');
    setResendMessage('');

    try {
      const response = await resendInvite(email);

      if (response.success) {
        const userType = response.data?.user?.userType;

        if (userType === 'federated') {
          setResendMessage('Resend not applicable for federated users. Please proceed to login.');
        } else {
          setResendMessage('An invitation email has been sent to your email.');
        }
      } else {
        setError(response.message || 'Failed to resend invitation');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to resend invitation');
    } finally {
      setIsResending(false);
    }
  };

  const handleFederatedRegistration = async (userEmail: string) => {
    try {
      const result = await selfRegistration(userEmail, true);

      if (result.success) {
        setView('success-federated');
      } else {
        setError(result.message || 'Registration failed');
      }
    } catch (err: any) {
      setError(err.message || 'Registration failed');
    }
  };

  const handleNonFederatedRegistration = async (e: FormEvent) => {
    e.preventDefault();
    setError('');

    // Validate all fields
    if (!formData.firstName.trim()) {
      setError('First name is required');
      return;
    }
    if (!formData.lastName.trim()) {
      setError('Last name is required');
      return;
    }
    if (!formData.phoneNumber.trim()) {
      setError('Phone number is required');
      return;
    }
    if (formData.phoneNumber.length < 3 || formData.phoneNumber.length > 17) {
      setError('Phone number must be between 3 and 17 characters');
      return;
    }
    if (!formData.password) {
      setError('Password is required');
      return;
    }
    if (passwordErrors.length > 0) {
      setError('Please fix password requirements');
      return;
    }

    setIsLoading(true);

    try {
      const result = await selfRegistration(formData.userEmail, false, formData);

      if (result.success) {
        setView('success-nonfederated');
      } else {
        setError(result.message || 'Registration failed');
      }
    } catch (err: any) {
      setError(err.message || 'Registration failed');
    } finally {
      setIsLoading(false);
    }
  };
  useEffect(() => {
    // Add class to body when component mounts
    document.body.classList.add('registration-page');
    document.body.style.overflow = 'auto';

    // Cleanup when component unmounts
    return () => {
      document.body.classList.remove('registration-page');
      document.body.style.overflow = '';
    };
  }, []);

  // Initial view - Email input
  // Initial view - Email input
  if (view === 'initial') {
    return (
      <div className="login-page-container">
        <div className="login-overlay"></div>

        <div className="login-modal-container">
          <div className="login-modal-content">

            {/* Header Section */}
            <div className="login-header-section">
              <div className="login-header-content">
                <h2 className="login-title">Welcome</h2>
                <p className="login-subtitle">Please sign in to access the dashboard</p>
              </div>
            </div>

            {/* Form Section */}
            <div className="login-form-section">
              <form className="login-form" onSubmit={handleEmailSubmit}>

                {/* Error Message */}
                {error && (
                  <div className="login-error-container">
                    <AlertCircle className="error-icon" />
                    <span className="error-text">{error}</span>
                  </div>
                )}

                {/* Success Message for Resend */}
                {resendMessage && (
                  <div className="login-error-container" style={{ backgroundColor: '#e8f5e9', borderColor: '#4caf50' }}>
                    <CheckCircle className="error-icon" style={{ color: '#4caf50' }} />
                    <span className="error-text" style={{ color: '#2e7d32' }}>{resendMessage}</span>
                  </div>
                )}

                {/* Email Field */}
                <div className="login-field-container">
                  <div className="login-input-wrapper">
                    <Mail className="input-icon" />
                    <input
                      type="email"
                      id="email"
                      value={email}
                      onChange={(e) => {
                        setEmail(e.target.value);
                        if (error) setError('');
                        if (resendMessage) setResendMessage('');
                        if (agreedToTerms) setAgreedToTerms(false);
                      }}
                      className="login-input"
                      placeholder="Enter your email address"
                      disabled={isLoading || isResending}
                      autoFocus
                    />
                  </div>
                </div>

                {/* Terms and Conditions Checkbox */}
                <div className="login-terms-container">
                  <label className="login-terms-label">
                    {/* <input
                      type="checkbox"
                      checked={agreedToTerms}
                      onChange={(e) => setAgreedToTerms(e.target.checked)}
                      className="login-terms-checkbox"
                      disabled={isLoading || isResending}
                    /> */}
                    <span className="login-terms-text">
                      By clicking <a
                        href="https://privacyhub.pwc.com/us/en/candidate-interview-scheduler/privacy-policy.html"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="login-terms-link"
                      >Submit </a> below, you confirm that you have read and agree to the Terms of Use and that you have read and understand the Privacy Statement
                    </span>
                  </label>
                </div>

                {/* Continue Button */}
                <div className="login-generate-token-container">
                  <button
                    type="submit"
                    className="login-submit-btn"
                    disabled={isLoading || isResending || !email.trim()}
                  // disabled={isLoading || isResending || !email.trim() || !agreedToTerms}
                  >
                    {isLoading ? (
                      <span className="loading-content">
                        <Loader2 className="loading-spinner" />
                        Verifying...
                      </span>
                    ) : (
                      'Submit'
                    )}
                  </button>
                </div>

                {/* Resend Invite Link */}
                <div className='resend-link'>
                  <p className="login-footer-text">
                    Resend invitation link to yourself?{' '}
                    <button
                      type="button"
                      className="linklike-btn"
                      onClick={handleResendInvite}
                      disabled={!email.trim() || isLoading || isResending}
                      style={{
                        opacity: (!email.trim() || isLoading || isResending) ? 0.5 : 1,
                        cursor: (!email.trim() || isLoading || isResending) ? 'not-allowed' : 'pointer'
                      }}
                    >
                      {isResending ? 'Sending...' : 'Click here'}
                    </button>
                  </p>
                </div>

              </form>
            </div>

            {/* Footer Section */}
            <div className="login-footer-section">
              {/* <p className="login-footer-text">
              Already have an account?{' '}
              <button type="button" className="linklike-btn" onClick={onLoginRedirect}>
                Proceed to Login
              </button>
            </p> */}
              {/* <p className="login-footer-text">
              Don't have an account?{' '}
              <button type="button" className="linklike-btn" onClick={onLoginRedirect}>
                Register
              </button>
            </p> */}
              <p className="login-footer-text mt-1">
                By continuing, you agree to PwC's Terms of Service and Privacy Policy
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Registration form for non-federated users
  if (view === 'register-form') {
    return (
      <div className="reg-login-container">
        <div className="reg-login-card reg-login-card-wide">
          <div className="reg-login-header">
            <h1 className="reg-login-title">Complete Your Registration</h1>
            <p className="reg-login-subtitle">Please provide the following details</p>
          </div>

          <form className="reg-login-form" onSubmit={handleNonFederatedRegistration}>
            {error && (
              <div className="reg-error-banner">
                <AlertCircle className="reg-error-icon" />
                <span className="reg-error-text">{error}</span>
              </div>
            )}

            <div className="reg-form-row">
              <div className="reg-form-group">
                <label htmlFor="firstName" className="reg-label">First Name *</label>
                <div className="reg-input-wrapper">
                  <User className="reg-input-icon" />
                  <input
                    type="text"
                    id="firstName"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    className="reg-input"
                    placeholder="Enter first name"
                    disabled={isLoading}
                  />
                </div>
              </div>

              <div className="reg-form-group">
                <label htmlFor="lastName" className="reg-label">Last Name *</label>
                <div className="reg-input-wrapper">
                  <User className="reg-input-icon" />
                  <input
                    type="text"
                    id="lastName"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    className="reg-input"
                    placeholder="Enter last name"
                    disabled={isLoading}
                  />
                </div>
              </div>
            </div>

            <div className="reg-form-group">
              <label htmlFor="email-display" className="reg-label">Email Address</label>
              <div className="reg-input-wrapper">
                <Mail className="reg-input-icon" />
                <input
                  type="email"
                  id="email-display"
                  value={formData.userEmail}
                  className="reg-input"
                  disabled
                />
              </div>
            </div>

            <div className="reg-form-group">
              <label htmlFor="phoneNumber" className="reg-label">Phone Number *</label>
              <div className="reg-input-wrapper">
                <Phone className="reg-input-icon" />
                <input
                  type="tel"
                  id="phoneNumber"
                  name="phoneNumber"
                  value={formData.phoneNumber}
                  onChange={handleInputChange}
                  className="reg-input"
                  placeholder="+1 234 567 8900"
                  disabled={isLoading}
                />
              </div>
              <p className="reg-field-hint">Include country code (3-17 characters)</p>
            </div>

            <div className="reg-form-group">
              <label htmlFor="password" className="reg-label">Password *</label>
              <div className="reg-input-wrapper">
                <Lock className="reg-input-icon" />
                <input
                  type={showPassword ? "text" : "password"}
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handlePasswordChange}
                  className="reg-input"
                  placeholder="Create a password"
                  disabled={isLoading}
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="reg-password-toggle"
                  disabled={isLoading}
                >
                  {showPassword ? <EyeOff className="reg-icon-sm" /> : <Eye className="reg-icon-sm" />}
                </button>
              </div>

              <div className="reg-password-requirements">
                <p className="reg-requirements-title">Password must contain:</p>
                <ul className="reg-requirements-list">
                  <li className={formData.password.length >= 8 && formData.password.length <= 64 ? 'reg-requirement-met' : ''}>
                    8-64 characters
                  </li>
                  <li className={
                    [/[a-z]/.test(formData.password), /[A-Z]/.test(formData.password), /[0-9]/.test(formData.password), /[@#$%^&*\-_!+=\[\]{}|\\:',.\?/`~"();<>]/.test(formData.password)]
                      .filter(Boolean).length >= 3 ? 'reg-requirement-met' : ''
                  }>
                    At least 3 of: lowercase, uppercase, number, special character
                  </li>
                </ul>
                {passwordErrors.length > 0 && (
                  <div className="reg-password-errors">
                    {passwordErrors.map((err, idx) => (
                      <p key={idx} className="reg-password-error">{err}</p>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <button
              type="submit"
              className="reg-submit-btn"
              disabled={isLoading || passwordErrors.length > 0}
            >
              {isLoading ? (
                <span className="reg-loading-content">
                  <Loader2 className="reg-loading-spinner" />
                  Registering...
                </span>
              ) : (
                'Complete Registration'
              )}
            </button>

            <button
              type="button"
              className="reg-secondary-btn"
              onClick={() => {
                setView('initial');
                setEmail('');
                setFormData({
                  userEmail: '',
                  firstName: '',
                  lastName: '',
                  phoneNumber: '',
                  password: ''
                });
                setError('');
              }}
              disabled={isLoading}
            >
              Back
            </button>
          </form>
        </div>
      </div>
    );
  }

  // Success view for federated users
  if (view === 'success-federated') {
    return (
      <div className="reg-login-container">
        <div className="reg-login-card">
          <div className="reg-success-icon-wrapper">
            <CheckCircle className="reg-success-icon" />
          </div>

          <div className="reg-login-header">
            <h1 className="reg-login-title">Registration Successful!</h1>
            <p className="reg-login-subtitle">
              Your account has been successfully registered. You can now login to access the application.
            </p>
          </div>

          <button
            type="button"
            className="reg-submit-btn"
            onClick={() => onLoginRedirect(email)}
          >
            Login
          </button>

          <div className="reg-footer">
            <p className="reg-footer-text">
              You will be redirected to the Microsoft login page
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Success view for non-federated users
  if (view === 'success-nonfederated') {
    return (
      <div className="reg-login-container">
        <div className="reg-login-card">
          <div className="reg-success-icon-wrapper">
            <Mail className="reg-success-icon reg-success-icon-mail" />
          </div>

          <div className="reg-login-header">
            <h1 className="reg-login-title">Check Your Email</h1>
            <p className="reg-login-subtitle">
              We've sent an activation link to <strong>{formData.userEmail}</strong>
            </p>
          </div>

          <div className="reg-info-box">
            <p className="reg-info-text">
              Please check your email and click on the activation link to complete your registration.
              The link will expire in 7 days.
            </p>
          </div>

          <button
            type="button"
            className="reg-secondary-btn"
            onClick={() => {
              setView('initial');
              setEmail('');
              setFormData({
                userEmail: '',
                firstName: '',
                lastName: '',
                phoneNumber: '',
                password: ''
              });
              setError('');
            }}
          >
            Back to Registration
          </button>

          <div className="reg-footer">
            <p className="reg-footer-text">
              Didn't receive the email? Check your spam folder or contact support
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Already registered view
  if (view === 'already-registered') {
    return (
      <div className="reg-login-container">
        <div className="reg-login-card">
          <div className="reg-success-icon-wrapper">
            <CheckCircle className="reg-success-icon" style={{ color: '#ff6f00' }} />
          </div>

          <div className="reg-login-header">
            <h1 className="reg-login-title">Already Registered</h1>
            <p className="reg-login-subtitle">
              The email <strong>{email}</strong> is already registered in the system.
            </p>
          </div>

          <div className="reg-info-box">
            <p className="reg-info-text">
              You can proceed directly to login using your registered credentials.
            </p>
          </div>

          <button
            type="button"
           className="reg-submit-btn"
            onClick={() => onLoginRedirect(email)}
          >
            Proceed to Login
          </button>

          {/* <button
          type="button"
          className="reg-secondary-btn"
          onClick={() => {
            setView('initial');
            setEmail('');
            setError('');
          }}
        >
          Try Different Email
        </button>

        <div className="reg-footer">
          <p className="reg-footer-text">
            Need help? Contact support
          </p>
        </div> */}
        </div>
      </div>
    );
  }

  return null;
}

export default Page_Registration_Login;